import { useApi } from '~/composables/common/use-api'
import { useCommonLobbyGames } from '~/composables/lobby-games/use-common-lobby-game'
import { useScrollIntoView } from '~/utils'
import { GAMES_API } from '~/configs/api'
import { LOBBY_GAME_URLS } from '~/configs/page-url'
import { GAMES_LIMIT } from '~/constants'
import { PROVIDERS } from '~/resources/lobby-games'
import type { GamesRequestPayload, GameItem } from '~composables/lobby-games/interface'
import type { DropdownItem } from '~/composables/common/interface'
import { GameProvider, HttpRequestMethod } from '~/configs/enums'
import { ResponseStatus } from '~composables/common/interface'
import { CATEGORY_GAME_SLUG } from '~/resources/lobby-games/urls'

interface GameIdResponseObject {
  gameId: string
  provider: string
}

interface FetchOptions {
  instantFetch?: boolean
}

const DEFAULT_PROVIDER: DropdownItem = {
  label: 'Tất cả',
  icon: '/assets/images/icons/provider/all.png',
  iconGray: '/assets/images/icons/provider/all-gray.webp',
  value: ''
}

export function useLobbyGames(fetchOption: FetchOptions = { instantFetch: true }) {
  const { instantFetch } = fetchOption

  const router = useRouter()
  const { request } = useApi()
  const { currentCategory, gameType, updateGameType } = useCommonLobbyGames()

  const gamePayload = ref<GamesRequestPayload>({
    cats: 'all',
    paged: 1,
    limit: GAMES_LIMIT,
    groups: '',
    prov: '',
    search: ''
  })
  const isLoadingGame = ref(true)
  const totalPost = ref(0)
  const lobbyGames = ref<GameItem[]>([])
  const lobbyGameRef = ref<HTMLDivElement | null>()
  const providerOptions = ref<DropdownItem[]>([DEFAULT_PROVIDER])

  async function fetchGames(payload: GamesRequestPayload): Promise<{ games: GameItem[]; total: number }> {
    try {
      const {
        data: games,
        metaData: { total }
      } = await request(GAMES_API.GAMES, { method: HttpRequestMethod.GET, params: payload }).catch(error => error.data)
      return { games: games.map((item: GameItem) => ({ ...item, gameUrl: item.gameUrl ?? item.link })), total }
    } catch {
      return { games: [], total: 0 }
    } finally {
      isLoadingGame.value = false
    }
  }

  const setProviders = async () => {
    try {
      const response = await request(GAMES_API.PROVIDERS, {
        method: HttpRequestMethod.GET,
        params: { cats: gamePayload.value.cats }
      })
      const { data: gameIdsResponse } = await request(GAMES_API.GAME_IDS_FILTER, {
        method: HttpRequestMethod.GET,
        params: { cats: gamePayload.value.cats }
      })
      const providerIdSet = new Set(gameIdsResponse.map((gameIdItem: GameIdResponseObject) => gameIdItem.provider))
      if (response.status === ResponseStatus.OK && response.data) {
        const providersHavingGames = new Set<string>()
        response.data.forEach(({ slug }: { slug: string }) => providersHavingGames.add(slug))
        providerOptions.value = [
          DEFAULT_PROVIDER,
          ...Array.from(providersHavingGames).reduce<DropdownItem[]>((result, providerSlug) => {
            if (providerIdSet.has(providerSlug)) {
              if (providerSlug === GameProvider.TECHPLAY && gamePayload.value.cats === CATEGORY_GAME_SLUG.NOHU) {
                return [
                  {
                    ...PROVIDERS[providerSlug as keyof typeof PROVIDERS],
                    labelStatement: 'Nổ Hũ'
                  },
                  ...result
                ]
              }
              return [...result, PROVIDERS[providerSlug as keyof typeof PROVIDERS]]
            }
            return result
          }, [])
        ]
      }
    } catch {
      providerOptions.value = [DEFAULT_PROVIDER]
    }
  }

  const handleScrollToGameSection = (): void => {
    if (router.currentRoute.value?.path.includes(LOBBY_GAME_URLS.ALL_GAME)) {
      setTimeout(() => {
        useScrollIntoView(lobbyGameRef.value)
      }, 100)
    }
  }

  const updateProvidersFilter = (selectedProvider: DropdownItem): void => {
    gamePayload.value = { ...gamePayload.value, prov: selectedProvider.value?.toString(), paged: 1 }
  }

  const loadMore = (): void => {
    gamePayload.value = { ...gamePayload.value, paged: (gamePayload.value.paged ?? 0) + 1 }
  }

  watch(
    () => gamePayload.value,
    async newPayload => {
      isLoadingGame.value = newPayload.paged === 1
      const { games, total } = await fetchGames(newPayload)
      if (newPayload.search) {
        newPayload.paged = 1
      }
      totalPost.value = total
      if (newPayload.paged === 1) {
        lobbyGames.value = games
      } else {
        lobbyGames.value = [...lobbyGames.value, ...games]
      }
    },
    { deep: true }
  )

  watch(
    () => router.currentRoute.value?.query?.type,
    tab => {
      const currentTab = (tab ?? '') as string
      updateGameType(currentTab)
      gamePayload.value = { ...gamePayload.value, paged: 1, cats: currentCategory.slug, prov: '', search: '' }
      void setProviders()
      handleScrollToGameSection()
    },
    { immediate: instantFetch }
  )

  return {
    loadMore,
    gameType,
    totalPost,
    lobbyGames,
    gamePayload,
    lobbyGameRef,
    isLoadingGame,
    providerOptions,
    fetchGames,
    updateProvidersFilter,
    handleScrollToGameSection
  }
}
